import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEventListener } from 'hooks/useEventListener';
import {
  Activity,
  ArrowUpRight,
  BookOpen,
  Brain,
  CalendarDays,
  Compass,
  Headphones,
  HeartPulse,
  Map,
  MessageCircleHeart,
  NotebookPen,
  Puzzle,
  Search,
  Sparkles,
  Star,
  Target
} from 'lucide-react';
import { AppPage, CalendarEvent, EIREntry, JournalNote } from 'types';

export interface SearchResult {
  id: string;
  title: string;
  description: string;
  category: string;
  page: AppPage;
  searchParams?: Record<string, string>;
  sectionSelector?: string;
  keywords?: string[];
  badge?: string;
  rank?: number;
  icon?: React.ReactNode;
}

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: SearchResult) => void;
  entries: EIREntry[];
  journalNotes: JournalNote[];
  calendarEvents: CalendarEvent[];
}

const createStaticIndex = (): SearchResult[] => [
  {
    id: 'page-ehr',
    title: 'Health Journey',
    description: 'View connected clinical records, timeline, and AI health analysis.',
    category: 'Pages',
    page: 'ehr',
    keywords: ['records', 'timeline', 'analysis', 'clinic', 'health journey'],
    icon: <NotebookPen size={18} color="#4f46e5" />,
    rank: 8
  },
  {
    id: 'page-health-assessment',
    title: 'Health Assessment',
    description: 'Guided self assessments with compassionate insights.',
    category: 'Pages',
    page: 'health-assessment',
    keywords: ['forms', 'assessment', 'screening'],
    icon: <Sparkles size={18} color="#10b981" />,
    rank: 6
  },
  {
    id: 'page-personal-growth',
    title: 'Improve your Health',
    description: 'Feedback Studio, Health Actions, Listen, Voice Lab, Health Literacy, CBT Programs, Brain Training, and Puzzle Studio.',
    category: 'Pages',
    page: 'personal-growth',
    keywords: ['improve', 'health', 'feedback', 'actions', 'listen', 'voice', 'literacy', 'cbt', 'brain', 'puzzle'],
    icon: <Activity size={18} color="#6366f1" />,
    rank: 7
  },
  {
    id: 'page-feedback',
    title: 'Feedback Studio',
    description: 'Share feedback and suggestions to improve your experience.',
    category: 'Improve your Health',
    page: 'personal-growth',
    searchParams: { tab: 'feedback' },
    keywords: ['feedback', 'studio', 'suggestions'],
    icon: <MessageCircleHeart size={18} color="#ec4899" />,
    rank: 6
  },
  {
    id: 'page-health-actions',
    title: 'Health Actions',
    description: 'Generate 15-minute habits and keep momentum with quick wins.',
    category: 'Improve your Health',
    page: 'personal-growth',
    searchParams: { tab: 'health-actions' },
    keywords: ['habits', 'tasks', 'actions'],
    icon: <Target size={18} color="#ec4899" />,
    rank: 6
  },
  {
    id: 'page-listen',
    title: 'Listen',
    description: 'Listening Practice Lounge with binaural beats and ambient sounds.',
    category: 'Improve your Health',
    page: 'personal-growth',
    searchParams: { tab: 'listen' },
    keywords: ['listen', 'binaural', 'beats', 'ambient', 'sound'],
    icon: <Headphones size={18} color="#0ea5e9" />,
    rank: 6
  },
  {
    id: 'page-voice',
    title: 'Voice Lab',
    description: 'Vocal analysis, pitch matching, and articulation exercises.',
    category: 'Improve your Health',
    page: 'personal-growth',
    searchParams: { tab: 'voice' },
    keywords: ['voice', 'vocal', 'pitch', 'articulation', 'singing'],
    icon: <Activity size={18} color="#f43f5e" />,
    rank: 6
  },
  {
    id: 'page-health-literacy',
    title: 'Health Literacy',
    description: 'Guides tailored to your health story with actionable tips.',
    category: 'Improve your Health',
    page: 'personal-growth',
    searchParams: { tab: 'health-literacy' },
    keywords: ['guides', 'articles', 'education', 'literacy'],
    icon: <BookOpen size={18} color="#334155" />,
    rank: 6
  },
  {
    id: 'page-health-programs',
    title: 'Health Programs',
    description: 'Structured programs to improve your health based on evidence-based principles like CBT.',
    category: 'Improve your Health',
    page: 'health-programs',
    keywords: ['cbt', 'therapy', 'program', 'health programs'],
    icon: <Brain size={18} color="#6366f1" />,
    rank: 5
  },
  {
    id: 'page-brain-training',
    title: 'Brain Training',
    description: 'Cognitive exercises and training games.',
    category: 'Improve your Health',
    page: 'personal-growth',
    searchParams: { tab: 'brain-training' },
    keywords: ['brain', 'training', 'cognitive', 'exercises'],
    icon: <Activity size={18} color="#8b5cf6" />,
    rank: 5
  },
  {
    id: 'page-puzzles',
    title: 'Puzzle Studio',
    description: 'Engaging puzzles and brain teasers.',
    category: 'Improve your Health',
    page: 'personal-growth',
    searchParams: { tab: 'puzzles' },
    keywords: ['puzzle', 'studio', 'brain', 'teaser'],
    icon: <Puzzle size={18} color="#f59e0b" />,
    rank: 5
  },
  {
    id: 'page-get-care',
    title: 'Get Care',
    description: 'Find healthcare facilities and request care referrals.',
    category: 'Pages',
    page: 'get-care',
    keywords: ['care', 'map', 'request', 'referral', 'healthcare'],
    icon: <Compass size={18} color="#f59e0b" />,
    rank: 4
  },
  {
    id: 'page-health-map',
    title: 'Health Map',
    description: 'Find clinics, hospitals, and support services near you.',
    category: 'Get Care',
    page: 'get-care',
    searchParams: { tab: 'health-map' },
    keywords: ['map', 'care', 'location', 'clinic', 'hospital'],
    icon: <Map size={18} color="#059669" />,
    rank: 4
  },
  {
    id: 'page-request-care',
    title: 'Request Care',
    description: 'Create and send a polished care request using your records.',
    category: 'Get Care',
    page: 'get-care',
    searchParams: { tab: 'request-care' },
    keywords: ['referral', 'care', 'request'],
    icon: <Compass size={18} color="#f59e0b" />,
    rank: 4
  },
  {
    id: 'page-eir-docs',
    title: 'Eir Docs',
    description: 'Explore the tutorial, documentation, app feedback, and product roadmap.',
    category: 'Pages',
    page: 'eir-docs',
    keywords: ['docs', 'tutorial', 'roadmap', 'documentation', 'feedback'],
    icon: <BookOpen size={18} color="#1d4ed8" />,
    rank: 3
  },
  {
    id: 'page-tutorial',
    title: 'Tutorial',
    description: 'Learn how to use Eir and get started with your health journey.',
    category: 'Eir Docs',
    page: 'eir-docs',
    searchParams: { tab: 'tutorial' },
    keywords: ['tutorial', 'learn', 'guide', 'getting started'],
    icon: <BookOpen size={18} color="#6366f1" />,
    rank: 3
  },
  {
    id: 'page-documentation',
    title: 'Documentation',
    description: 'Technical documentation and detailed guides.',
    category: 'Eir Docs',
    page: 'eir-docs',
    searchParams: { tab: 'documentation' },
    keywords: ['documentation', 'docs', 'technical', 'guides'],
    icon: <BookOpen size={18} color="#334155" />,
    rank: 3
  },
  {
    id: 'page-app-feedback',
    title: 'App Feedback',
    description: 'Share your feedback and suggestions to improve the app.',
    category: 'Eir Docs',
    page: 'eir-docs',
    searchParams: { tab: 'app-feedback' },
    keywords: ['feedback', 'suggestions', 'improve', 'app'],
    icon: <Star size={18} color="#f59e0b" />,
    rank: 3
  },
  {
    id: 'page-roadmap',
    title: 'Roadmap',
    description: 'View the product roadmap and upcoming features.',
    category: 'Eir Docs',
    page: 'eir-docs',
    searchParams: { tab: 'roadmap' },
    keywords: ['roadmap', 'features', 'upcoming', 'product'],
    icon: <Map size={18} color="#0ea5e9" />,
    rank: 3
  }
];

const buildSearchableItems = (
  entries: EIREntry[],
  journalNotes: JournalNote[],
  calendarEvents: CalendarEvent[]
): SearchResult[] => {
  const clinicalItems: SearchResult[] = entries.map(entry => {
    const title = entry.content?.summary || entry.type || 'Clinical entry';
    const meta = [entry.date, entry.category, entry.provider?.name].filter(Boolean).join(' • ');
    const keywords: string[] = [];
    if (entry.content?.details) keywords.push(entry.content.details);
    if (entry.content?.notes) {
      if (Array.isArray(entry.content.notes)) {
        keywords.push(...entry.content.notes);
      } else {
        keywords.push(entry.content.notes);
      }
    }
    return {
      id: `entry-${entry.id}`,
      title,
      description: meta || 'Connected health record',
      category: 'Your records',
      page: 'ehr',
      searchParams: entry.id ? { entryId: entry.id } : undefined,
      keywords,
      icon: <NotebookPen size={16} color="#4f46e5" />,
      rank: 6
    };
  });

  const noteItems: SearchResult[] = journalNotes.map(note => ({
    id: `note-${note.id}`,
    title: note.title || 'Health journal entry',
    description: new Date(note.createdAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }),
    category: 'Your journal',
    page: 'journal',
    searchParams: { noteId: note.id },
    keywords: [note.content, String(note.wellbeingScore)],
    icon: <HeartPulse size={16} color="#f43f5e" />,
    rank: 5
  }));

  const calendarItems: SearchResult[] = calendarEvents.map(event => ({
    id: `calendar-${event.id}`,
    title: event.title,
    description: `${new Date(event.start).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })} → ${new Date(event.end).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`,
    category: 'Calendar',
    page: 'calendar',
    searchParams: { focus: event.id },
    keywords: [event.description ?? '', event.source],
    icon: <CalendarDays size={16} color="#2563eb" />,
    rank: 4
  }));

  return [...createStaticIndex(), ...clinicalItems, ...noteItems, ...calendarItems];
};

const scoreResult = (item: SearchResult, queryTokens: string[]): number => {
  const haystack = `${item.title} ${item.description} ${item.category} ${(item.keywords || []).join(' ')}`.toLowerCase();
  const matches = queryTokens.reduce((count, token) => count + (haystack.includes(token) ? 1 : 0), 0);
  const baseRank = item.rank ?? 1;
  return baseRank * 10 + matches;
};

export const GlobalSearch: React.FC<GlobalSearchProps> = ({
  isOpen,
  onClose,
  onSelect,
  entries,
  journalNotes,
  calendarEvents
}) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const searchIndex = useMemo(
    () => buildSearchableItems(entries, journalNotes, calendarEvents),
    [entries, journalNotes, calendarEvents]
  );

  const normalizedQuery = query.trim().toLowerCase();
  const queryTokens = useMemo(() => normalizedQuery.split(/\s+/).filter(Boolean), [normalizedQuery]);

  const filteredResults = useMemo(() => {
    if (queryTokens.length === 0) {
      return [...searchIndex].sort((a, b) => (b.rank ?? 0) - (a.rank ?? 0)).slice(0, 18);
    }
    return searchIndex
      .filter(item => queryTokens.every(token =>
        `${item.title} ${item.description} ${item.category} ${(item.keywords || []).join(' ')}`
          .toLowerCase()
          .includes(token)
      ))
      .map(item => ({ item, score: scoreResult(item, queryTokens) }))
      .sort((a, b) => b.score - a.score)
      .map(({ item }) => item)
      .slice(0, 24);
  }, [queryTokens, searchIndex]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEventListener('keydown', (event: KeyboardEvent) => {
    if (!isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
    if (event.key === 'Enter' && filteredResults[0]) {
      onSelect(filteredResults[0]);
    }
  });

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-slate-900/55 backdrop-blur-sm flex items-start justify-center p-6 pt-[clamp(3rem,8vw,6rem)] overflow-y-auto z-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Universal search"
      onClick={handleBackdropClick}
    >
      <div className="w-[min(1100px,100%)] bg-card rounded-xl border-primary/15 shadow-lg p-6 grid gap-4">
        <div className="flex items-center gap-3 justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-primary" />
            <strong className="text-foreground-strong">Search everything</strong>
          </div>
          <span className="inline-flex items-center gap-1.5 text-sm text-foreground-muted py-1.5 px-2.5 rounded-lg bg-muted border">
            <span className="inline-flex w-3 h-3 rounded-full bg-primary" />
            Press Enter to open top result
          </span>
        </div>

        <div className="flex items-center gap-3 bg-card rounded-xl border py-3.5 px-4 shadow-lg">
          <Search size={18} className="text-foreground-muted" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, records, puzzles, notes, and more"
            aria-label="Search"
            className="flex-1 border-none outline-none text-base bg-transparent text-foreground-strong"
          />
          <span className="inline-flex items-center gap-1.5 text-sm text-foreground-muted py-1.5 px-2.5 rounded-lg bg-muted border">
            Ctrl/Cmd + K
          </span>
        </div>

        {filteredResults.length === 0 ? (
          <div className="text-center text-foreground-muted py-10 px-4 border rounded-xl bg-muted grid gap-2">
            <strong>No results yet</strong>
            <span>Try a different keyword or keep typing to surface more of your workspace.</span>
          </div>
        ) : (
          <div className="grid gap-3">
            {filteredResults.map(result => (
              <button
                type="button"
                className="border border bg-card rounded-xl p-4 grid gap-2.5 text-left cursor-pointer hover:border-primary/30 hover:shadow-md transition-all"
                key={result.id}
                onClick={() => onSelect(result)}
              >
                <div className="flex items-center gap-2 font-bold text-foreground-strong">
                  {result.icon ?? <Sparkles size={16} className="text-primary" />}
                  <span>{result.title}</span>
                </div>
                <p className="m-0 text-foreground-muted text-sm leading-normal">{result.description}</p>
                <div className="flex items-center gap-2 justify-between text-foreground-muted text-sm">
                  <span className="inline-flex items-center gap-1.5 py-1 px-2.5 rounded-full bg-primary/10 text-primary font-semibold text-xs">
                    {result.category}
                  </span>
                  <div className="inline-flex items-center gap-1.5">
                    {result.badge && (
                      <span className="inline-flex items-center gap-1 py-1 px-2 rounded-lg bg-muted text-foreground font-semibold text-xs">
                        {result.badge}
                      </span>
                    )}
                    <ArrowUpRight size={16} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
